/**
 * Operator and legal contact details, in one place.
 *
 * HealthPass is sold worldwide by a single US company — the same entity the
 * Stripe account belongs to, running on servers in Virginia. That shapes the
 * legal pages: the imprint is provider identification for a US company rather
 * than a German section 5 DDG entry, the contract runs under US law, and the
 * GDPR still applies because the service is offered to travellers in the EU
 * (Article 3(2)) — which is why a US controller needs an EU representative
 * under Article 27 and why that is a field here rather than an afterthought.
 *
 * The Imprint, the Privacy Policy and the Terms are read side by side by
 * regulators and app-store reviewers, so a mismatch between them is a finding.
 * Keeping the details here means there is one value to change.
 *
 * Anything still marked TO_BE_COMPLETED is a detail only the operator has.
 * `isLegalIdentityComplete` reports whether they have all been filled in; the
 * legal pages show an unmistakable notice while they have not, so an
 * incomplete imprint cannot pass for a finished one.
 */

import { parseDateValue } from "@/utils/dateLocalization";

export const TO_BE_COMPLETED = "«to be completed»";

export const LEGAL = {
  /** Effective date shown on every legal page (ISO, rendered per locale). */
  effectiveDate: "2026-10-06",

  /**
   * The company that sells and operates HealthPass, for every customer.
   *
   * Name, address and phone are the ones registered on the Stripe account
   * that takes the money, so the imprint and the card statement agree.
   */
  operator: {
    /** Registered company name including legal form. */
    name: "Centrafox Consulting LLC",
    /** Street and number. */
    street: "1209 Mountain Road Pl NE",
    /** City, state and ZIP. */
    city: "Albuquerque, NM 87110",
    country: "United States",
    /**
     * State of incorporation. Taken from where the company is based, which is
     * usually but not always the state it was formed in — worth checking
     * against the formation certificate, because section 11 of the Terms
     * chooses New Mexico law and courts on the strength of it.
     */
    stateOfIncorporation: "New Mexico",
    phone: "+1 617 446 1399",
  },

  /**
   * Representative in the Union under Article 27 GDPR. A controller outside
   * the EU that offers services to people in the EU must designate one in
   * writing and name it here; travellers and supervisory authorities are
   * entitled to address it instead of the US company.
   */
  euRepresentative: {
    name: "INNOVIZR.TEAM UG",
    street: "Hebbelstr. 16",
    city: "14469 Potsdam",
    country: "Germany",
    email: "privacy@innovizr.team",
  },

  /**
   * Who sells the paid plans. Under Stripe Managed Payments the seller of
   * record is Stripe's subsidiary below, shown to buyers as "Link" and on
   * their statement as "LINK.COM* …"; its "Sold through Link" terms govern the
   * purchase. Taken from link.com/terms ("Purchase Terms") and Stripe's
   * Managed Payments documentation on 2026-10-06.
   */
  reseller: {
    name: "Sold through Link, LLC",
    brand: "Link",
    termsUrl: "https://link.com/terms",
    supportUrl: "https://support.link.com/topics/sold-through-link",
  },

  /** Where users reach us. These mailboxes must exist before launch. */
  contact: {
    general: "support@travelhealthpass.app",
    privacy: "privacy@travelhealthpass.app",
    /** Data protection officer, or the privacy address if none is appointed. */
    dpo: "dpo@travelhealthpass.app",
  },

  domain: "travelhealthpass.app",
} as const;

/** Every operator detail filled in? The legal pages warn while this is false. */
export function isLegalIdentityComplete(): boolean {
  const values: string[] = [
    ...Object.values(LEGAL.operator),
    ...Object.values(LEGAL.euRepresentative),
  ];
  return values.every((value) => value !== TO_BE_COMPLETED);
}

/**
 * The effective date, written for a reader. Deliberately not the app language:
 * the legal texts exist in English only, so their date is written in the same
 * (US) English. Parsed as a calendar day — `new Date("2026-10-06")` is UTC
 * midnight, which every US timezone would show as October 5.
 */
export function formatLegalDate(locale = "en-US"): string {
  const date = parseDateValue(LEGAL.effectiveDate) ?? new Date(NaN);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
